import {
  AfterViewInit,
  Directive,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Renderer2
} from '@angular/core';

const DEFAULT_HEADER_SELECTOR = '.header-container';
const DEFAULT_BODY_SELECTORS = [
  '.body-container',
  '.cdk-virtual-scroll-viewport',
  '.md-virtual-repeat-scroller'
];

/**
 * Keeps a detached table header aligned with the scrolling table body:
 *  - mirrors the body horizontal scroll offset on the header
 *  - reserves the body vertical scrollbar width on the header, so columns stay aligned
 *
 * Place it on the element that contains both the header and the body
 * (the .table-outer-wrapper element), next to [tableColumnManager] on the header.
 */
@Directive({
  standalone: false,
  selector: '[tableScrollSync]'
})
export class TableScrollSyncDirective implements AfterViewInit, OnDestroy {

  @Input() headerSelector: string = DEFAULT_HEADER_SELECTOR;
  @Input() bodySelector: string;

  private headerEl: HTMLElement | null = null;
  private bodyEl: HTMLElement | null = null;
  private unlistenScroll: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;

  constructor(@Inject(ElementRef) private el: ElementRef,
              @Inject(Renderer2) private renderer: Renderer2,
              @Inject(NgZone) private zone: NgZone) {
  }

  ngAfterViewInit(): void {
    this._connect();
    this._observeStructureChanges();
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this._disconnect();
  }

  private _connect(): boolean {
    const host = this.el.nativeElement as HTMLElement;
    const headerEl = host.querySelector(this.headerSelector) as HTMLElement;
    const bodyEl = this._findBodyElement(host);

    if (!headerEl || !bodyEl) {
      return false;
    }
    if (headerEl === this.headerEl && bodyEl === this.bodyEl) {
      return true;
    }

    this._disconnect();
    this.headerEl = headerEl;
    this.bodyEl = bodyEl;

    // Scrolling only mutates scrollLeft / padding, never a binding - keep it out of change detection
    this.zone.runOutsideAngular(() => {
      this.unlistenScroll = this.renderer.listen(this.bodyEl, 'scroll', () => this._syncScrollLeft());
      this._observeSizeChanges();
    });

    this._syncScrollLeft();
    this._syncScrollbarGutter();
    return true;
  }

  private _disconnect(): void {
    if (this.unlistenScroll) {
      this.unlistenScroll();
      this.unlistenScroll = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.headerEl = null;
    this.bodyEl = null;
  }

  private _findBodyElement(host: HTMLElement): HTMLElement | null {
    const selectors = this.bodySelector ? [this.bodySelector] : DEFAULT_BODY_SELECTORS;
    for (const selector of selectors) {
      const found = host.querySelector(selector) as HTMLElement;
      if (found) {
        return found;
      }
    }
    return null;
  }

  /**
   * The body element is often rendered later than the header (*ngIf on an empty list,
   * md-virtual-repeat building its scroller), so watch for it appearing / being replaced.
   * Only direct children are observed - row rendering inside the body must not trigger this.
   */
  private _observeStructureChanges(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }
    this.mutationObserver = new MutationObserver(() => this._connect());
    this.mutationObserver.observe(this.el.nativeElement, {childList: true});
  }

  private _observeSizeChanges(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this._syncScrollbarGutter());
    this.resizeObserver.observe(this.bodyEl);

    const innerWrapper = this.bodyEl.querySelector('.table-inner-wrapper') as HTMLElement;
    if (innerWrapper) {
      this.resizeObserver.observe(innerWrapper);
    }
  }

  private _syncScrollLeft(): void {
    if (this.headerEl && this.bodyEl) {
      this.headerEl.scrollLeft = this.bodyEl.scrollLeft;
    }
  }

  private _syncScrollbarGutter(): void {
    if (!this.headerEl || !this.bodyEl) {
      return;
    }
    const scrollbarWidth = this.bodyEl.offsetWidth - this.bodyEl.clientWidth;
    this.headerEl.style.paddingRight = `${scrollbarWidth}px`;
  }
}
